import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Tooltip } from 'react-tooltip';
import { Pagination } from "../../../../common/pagination";
import ModernPagination from "../../../../common/pagination/ModernPagination";
import Table from "../../../../common/element/Table";
import ClientInput from "../../../../common/element/Input/clientInput";
import Switcher from "../../../../common/element/Switcher";
import { debounce } from "../../../../common/snippet/Debouncing";

import { useDispatch, useSelector } from 'react-redux';
import { selectUserData } from "../../../../redux/auth/authSelector";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from '../../../../common/Common/ConfirmationModal';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// ✅ React Datepicker imports
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
  getCompanySessions,
  enableCompanySession,
  enableToolAccess,
  enableSupportDocument,
  enableAssignment
} from '../../../../redux/CompanySession/companiesSessionSlice';
import {
  selectCompanySessionsList,
  selectCompanySessionsLoading,
  selectCompanySessionsTotal,
} from '../../../../redux/CompanySession/companiesSessionSelector';

// ✅ COACH IMPORTS
import { getAssociatedCompanies } from '../../../../redux/Coach/coachSlice';
import { selectAssociatedCompanies } from '../../../../redux/Coach/coachSelector';

import SweetAlertService from '../../../../common/SweetAlert/SweetAlert';
import Heading from '../../../../common/element/Heading';
import { setBreadcrumbs } from '../../../../redux/Admin/breadcrumbsSlice';
import Select from '../../../../common/element/Select';
import { formatToolName } from '../../../../common/Formatters.js/formatters';
import { setsessionName } from '../../../../redux/Admin/sessionViewSlice';
import CommonIconButton from '../../../../common/icons/CommonIconButton';
import CopyLinkURLModal from '../../Admin/ProspectCompany/CopyLink';

// Extend dayjs
dayjs.extend(customParseFormat);

const { Tr, Th, Td, THead, TBody } = Table;

const BatchesSessions = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [countData, setCountData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const filterRef = useRef(null);

  // Loading states
  const [toolToggleLoading, setToolToggleLoading] = useState({});
  const [docToggleLoading, setDocToggleLoading] = useState({});
  const [assignmentLoading, setAssignmentLoading] = useState({});

  // Selectors
  const associatedCompanies = useSelector(selectAssociatedCompanies);
  const sessionsFromStore = useSelector(selectCompanySessionsList);
  const sessionsLoading = useSelector(selectCompanySessionsLoading);
  const sessionsTotal = useSelector(selectCompanySessionsTotal);

  // ✅ 1. Filter State (Local state for inputs)
  const [filters, setFilters] = useState({
    companyId: null, // Stores the Select Option Object or ID
    dateRange: [null, null]
  });
  const [startDate, endDate] = filters.dateRange;

  // ✅ 2. Applied Filter State (Sent to API)
  const [apiParams, setApiParams] = useState({
    page: 1,
    limit: 10,
    search: "",
    company_id: null,
    from_date: null,
    to_date: null
  });

  const [companyOptions, setCompanyOptions] = useState([]);
  const [allSessionsData, setAllSessionsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCopyLinkModal, setOpenCopyLinkModal] = useState(false);
  const [selectedSessionLink, setSelectedSessionLink] = useState(null);

  // --- Initial Data Fetching ---

  useEffect(() => {
    dispatch(setBreadcrumbs([{ label: "Sessions", url: "/allSessions", home: "/dashboard" }]));
    // Fetch filter options
    dispatch(getAssociatedCompanies());
  }, [dispatch]);

  // Populate Company Options
  useEffect(() => {
    if (associatedCompanies && associatedCompanies.length > 0) {
      const options = associatedCompanies.map((comp) => ({
        value: comp.company_id || comp.id,
        label: comp.company_name || comp.name
      }));
      setCompanyOptions(options);
    }
  }, [associatedCompanies]);

  // --- Search Handler ---
  const handleSearch = (e) => {
    setApiParams((prev) => ({
      ...prev,
      page: 1,
      search: e.target.value.trim(),
    }));
  };
  const inputchangehandler = useCallback(debounce(handleSearch), []);
  const handleCompanyChange = (selectedOption) => {
    setFilters(prev => ({ ...prev, companyId: selectedOption }));
  };

  const handleDateChange = (update) => {
    setFilters(prev => ({ ...prev, dateRange: update }));
  };

  const applyFilters = () => {
    const [start, end] = filters.dateRange;

    const rawCompany = filters.companyId;
    const companyIdValue = rawCompany?.value ?? rawCompany;

    setApiParams(prev => ({
      ...prev,
      page: 1, // Reset page
      company_id: companyIdValue,
      from_date: start ? dayjs(start).format('YYYY-MM-DD') : null,
      to_date: end ? dayjs(end).format('YYYY-MM-DD') : null,
    }));

    setFilterOpen(false);
  };

  const clearFilters = () => {
    setFilters({
      companyId: null,
      dateRange: [null, null]
    });

    setApiParams(prev => ({
      ...prev,
      page: 1,
      company_id: null,
      from_date: null,
      to_date: null
    }));
  };

  useEffect(() => {
    const payload = {
      page: apiParams.page,
      limit: apiParams.limit,
      search: apiParams.search,
      company_id: apiParams.company_id,
      from_date: apiParams.from_date,
      to_date: apiParams.to_date
    };

    dispatch(getCompanySessions(payload));

  }, [dispatch, apiParams]);

  const refreshSessionsData = () => {
    dispatch(getCompanySessions({
      page: apiParams.page,
      limit: apiParams.limit,
      search: apiParams.search,
      company_id: apiParams.company_id,
      from_date: apiParams.from_date,
      to_date: apiParams.to_date
    }));
  };

  useEffect(() => {
    const mapped = Array.isArray(sessionsFromStore)
      ? sessionsFromStore.map((cs) => {
        const session = cs.session || {};
        const coachUser = cs.coach?.user || {};
        const batches = Array.isArray(cs.batches) ? cs.batches.map((b) => b.batch_name).join(', ') : '';
        const assignments = Array.isArray(cs.assignments) ? cs.assignments : [];
        const assignmentNames = assignments.map(a => a.assignment_name?.trim()).filter(Boolean).join(', ');
        const formattedDate = formatDateTime(cs.session_date, cs.session_time);

        return {
          id: cs.company_session_id ?? session.session_id,
          companyId: cs.company?.company_id,
          companyName: cs.company?.company_name || 'N/A',
          sessionName: cs.session_name ?? '',
          date: formattedDate,
          batch: batches,
          coach: `${coachUser.first_name ?? ''} ${coachUser.last_name ?? ''}`.trim(),
          status: cs.status ?? '',
          toolName: session.tool?.tool ?? session.tool ?? '',
          supportedDocEnabled: !!cs.supported_doc_enabled,
          assignmentEnabled: !!cs.assignment_enabled,
          assignStatus: !!cs.assign_status,
          toolEnabled: cs.tool_enabled === undefined ? false : !!cs.tool_enabled,
          assignments: assignments,
          assignmentNames: assignmentNames,
          sessionUrl: cs.session_url || '',
          _raw: cs,
        };
      })
      : [];

    setAllSessionsData(mapped);
    setCountData(sessionsTotal || 0);
    const totalPagesFromApi = apiParams.limit ? Math.ceil((sessionsTotal || 0) / apiParams.limit) : 0;
    setTotalPages(totalPagesFromApi);
    setLoading(!!sessionsLoading);
  }, [sessionsFromStore, sessionsLoading, sessionsTotal, apiParams.limit]);

  // --- Helper Functions ---

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const parsedDate = dayjs(dateString);
    return parsedDate.isValid() ? parsedDate.format('DD/MM/YYYY') : dateString;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed": return "text-green-600 bg-green-50 ";
      case "upcoming": return "text-blue-600 bg-blue-50";
      case "cancelled": return "text-red-600 bg-red-50";
      case "ongoing": return "text-[#CA8A04] bg-[#FEF9C3]";
      default: return "text-gray-600 bg-gray-50";
    }
  };

  const truncateText = (text, maxLength = 20) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  // ✅ Helper function to get tooltip content for toggles (Added Logic)
  const getToggleTooltip = (type, item) => {
    const status = item.status?.toLowerCase();

    // Priority Check: If cancelled
    if (status === 'cancelled') {
      return 'Session is cancelled, actions are disabled';
    }

    // Priority Check: If completed
    if (status === 'completed') {
      return 'Session is completed, actions are disabled';
    }

    if (type === 'docs' && item.supportedDocEnabled) {
      return 'Support document is sent and cannot be unsent';
    }
    if (type === 'tool' && item.toolEnabled) {
      return 'Tool is sent and cannot be unsent';
    }
    return '';
  };

  const handlePageChange = (page) => {
    setApiParams((prev) => ({ ...prev, page }));
  };

  // Click outside listener for filter
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setFilterOpen(false);
      }
    };
    if (filterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filterOpen]);


  // --- Action Handlers (Toggle/Status) ---

  const onToolToggle = async (checked, sessionId) => {
    const sessionItem = allSessionsData.find(s => s.id === sessionId);
    if (!sessionItem) return;
    const targetCompanyId = sessionItem.companyId || apiParams.company_id;

    // ✅ Prevent disabling - only allow enabling
    if (!checked && sessionItem.toolEnabled) {
      SweetAlertService.showWarning({ title: 'Action Not Allowed', message: 'Tool access cannot be disabled once enabled.' });
      return;
    }

    setToolToggleLoading(prev => ({ ...prev, [sessionId]: true }));
    try {
      const toolData = {
        company_session_id: sessionItem._raw.company_session_id,
        tool_enabled: checked,
        tool_id: sessionItem._raw.session?.tool?.id || sessionItem._raw.tool_id,
        batch_ids: sessionItem._raw.batches?.map(b => b.batch_id) || [],
        is_locked: !checked
      };
      const resultAction = await dispatch(enableToolAccess({ companyId: targetCompanyId, toolData }));
      if (enableToolAccess.fulfilled.match(resultAction)) {
        SweetAlertService.showSuccess({ title: 'Success!', message: 'Tool access enabled successfully.' });
        refreshSessionsData();
      } else {
        const err = resultAction.payload || resultAction.error;
        SweetAlertService.showError({ title: 'Error!', message: err?.message || 'Failed to enable tool access' });
      }
    } catch (err) {
      SweetAlertService.showError({ title: 'Error!', message: 'Failed to enable tool access' });
    } finally {
      setToolToggleLoading(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const onResourceToggle = async (checked, sessionId) => {
    const sessionItem = allSessionsData.find(s => s.id === sessionId);
    if (!sessionItem) return;
    const targetCompanyId = sessionItem.companyId || apiParams.company_id;

    // ✅ Prevent disabling - only allow enabling
    if (!checked && sessionItem.supportedDocEnabled) {
      SweetAlertService.showWarning({ title: 'Action Not Allowed', message: 'Support document access cannot be disabled once enabled.' });
      return;
    }

    setDocToggleLoading(prev => ({ ...prev, [sessionId]: true }));
    try {
      const documentData = {
        company_session_id: sessionItem._raw.company_session_id,
        supported_doc_enabled: checked,
        supported_doc_id: sessionItem._raw.supported_doc_id || sessionItem._raw.session?.supported_doc_id,
        batch_ids: sessionItem._raw.batches?.map(b => b.batch_id) || []
      };
      const resultAction = await dispatch(enableSupportDocument({ companyId: targetCompanyId, documentData }));
      if (enableSupportDocument.fulfilled.match(resultAction)) {
        SweetAlertService.showSuccess({ title: 'Success!', message: 'Support document enabled successfully.' });
        refreshSessionsData();
      } else {
        const err = resultAction.payload || resultAction.error;
        SweetAlertService.showError({ title: 'Error!', message: err?.message || 'Failed to enable support document' });
      }
    } catch (err) {
      SweetAlertService.showError({ title: 'Error!', message: 'Failed to enable support document' });
    } finally {
      setDocToggleLoading(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const handleSendAssignment = async (sessionId) => {
    const sessionItem = allSessionsData.find(s => s.id === sessionId);
    if (!sessionItem) return;
    const targetCompanyId = sessionItem.companyId || apiParams.company_id;

    // ✅ Prevent if already sent
    if (sessionItem.assignmentEnabled) {
      SweetAlertService.showWarning({ title: 'Already Sent', message: 'Assignment has already been sent.' });
      return;
    }

    setAssignmentLoading(prev => ({ ...prev, [sessionId]: true }));
    try {
      const assignmentIds = sessionItem._raw.assignments?.map(a => a.assignment_id) || [];
      const assignmentData = {
        company_session_id: sessionItem._raw.company_session_id,
        assignment_enabled: true,
        assignment_id: assignmentIds.length > 0 ? assignmentIds[0] : sessionItem._raw.assignment_id,
        batch_ids: sessionItem._raw.batches?.map(b => b.batch_id) || []
      };
      const resultAction = await dispatch(enableAssignment({ companyId: targetCompanyId, assignmentData }));
      if (enableAssignment.fulfilled.match(resultAction)) {
        SweetAlertService.showSuccess({ title: 'Success!', message: 'Assignment sent successfully.' });
        refreshSessionsData();
      } else {
        const err = resultAction.payload || resultAction.error;
        SweetAlertService.showError({ title: 'Error!', message: err?.message || 'Failed to send assignment' });
      }
    } catch (err) {
      SweetAlertService.showError({ title: 'Error!', message: 'Failed to send assignment' });
    } finally {
      setAssignmentLoading(prev => ({ ...prev, [sessionId]: false }));
    }
  };

  const handleAssignStatus = async (sessionId, status) => {
    const sessionItem = allSessionsData.find(s => s.id === sessionId);
    if (!sessionItem) return;
    const targetCompanyId = sessionItem.companyId || apiParams.company_id;

    try {
      const body = {
        company_session_id: sessionId,
        assign_status: status,
        ...(status === true && { mark_completed: status }),
      };
      const resultAction = await dispatch(enableCompanySession({ companyId: targetCompanyId, body }));
      if (enableCompanySession.fulfilled.match(resultAction)) {
        refreshSessionsData();
        SweetAlertService.showSuccess({ message: `Assignment status updated successfully.` });
      } else {
        const err = resultAction.payload || resultAction.error;
        SweetAlertService.showError({ message: err?.message || 'Failed to update assignment status' });
      }
    } catch (err) {
      SweetAlertService.showError({ message: 'Failed to update assignment status' });
    }
  };

  const startIndex = (apiParams.page - 1) * apiParams.limit;

  return (
    <React.Fragment>
      <Tooltip
        id="session-tooltip"
        place="top"
        className="!bg-gray-900 !text-white !text-xs !rounded-lg !px-3 !py-2 !z-50 !max-w-[250px]"
      />

      <div className="flex justify-between mb-5">
        <div className="w-80 h-10 text-start">
          <Heading>Sessions</Heading>
        </div>
      </div>

      <div className="flex justify-between items-end mb-4">
        <p className="font-lato not-italic font-semibold text-[30px] leading-[38px] text-[#101828] my-3 text-start"></p>
        <div className="flex gap-3 items-center">

          {/* Filter Dropdown */}
          <div className="relative" ref={filterRef}>
            <button
              onClick={() => setFilterOpen(!filterOpen)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors h-10"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 10H15M2.5 5H17.5M7.5 15H12.5" stroke="#344054" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="text-sm font-medium text-gray-700">Filter</span>
            </button>

            {filterOpen && (
              <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <div className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-base font-semibold text-gray-900">Filter</h3>
                    <button onClick={clearFilters} className="text-sm text-[#FF8E1C] hover:text-[#E67D0F] font-medium">Clear</button>
                  </div>
                  <div className="space-y-4 text-start">

                    {/* Company Selector */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
                      <Select
                        placeholder="Select Company"
                        options={companyOptions}
                        value={filters.companyId}
                        onChange={handleCompanyChange}
                      />
                    </div>

                    {/* ✅ React DatePicker for Range */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                      <div className="relative w-full">
                        <DatePicker
                          selectsRange={true}
                          startDate={startDate}
                          endDate={endDate}
                          onChange={handleDateChange}
                          placeholderText="From - To"
                          isClearable={true}
                          className="w-full input px-3 input-shadow py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#FF8E1C] focus:border-[#FF8E1C] text-sm"
                          dateFormat="dd/MM/yyyy"
                        />
                      </div>
                    </div>

                    <button onClick={applyFilters} className="w-full bg-[#FF8E1C] text-white py-2 rounded-lg hover:bg-[#E67D0F] transition-colors font-medium">Filter</button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Search Input */}
          <div className="input-wrapper">
            <div className="input-suffix-start left-3.5">
              <img src="/assets/icons-svg/search-lg.svg" alt="" className="h-5" />
            </div>
            <ClientInput
              type="text"
              className="input pl-10 w-80 input-shadow h-10 text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Search"
              inputchangehandler={inputchangehandler}
              name={"search"}
            />
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="border rounded-lg border-gray-200">
        <div className="overflow-x-auto">
          <Table>
            <THead>
              <Tr className="bg-gray-50">
                <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">S No.</Th>
                <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Company Name</Th>
                <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Session Name</Th>
                <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Date </Th>
                <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Batch</Th>
                <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Status</Th>
                <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Tool Name</Th>
                <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Supported Documents</Th>
                <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Assignment</Th>
                <Th className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Tool </Th>
                <Th className="text-center px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Status</Th>
                <Th className="text-center px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#475467] whitespace-nowrap">Actions</Th>
              </Tr>
            </THead>

            <TBody>
              {allSessionsData?.length > 0 ? (
                allSessionsData.map((item, index) => {
                  const status = item.status?.toLowerCase();
                  // Check if actions should be disabled based on status (completed or cancelled)
                  const areActionsDisabled = status === 'completed' || status === 'cancelled';

                  return (
                    <Tr key={item.id} className="border-b hover:bg-gray-50">
                      <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">
                        {String(startIndex + index + 1).padStart(2, "0")}
                      </Td>
                      <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">
                        {item.companyName}
                      </Td>
                      <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">{item.sessionName}</Td>
                      <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">{item.date}</Td>
                      <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">{item.batch}</Td>
                      <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </Td>
                      <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">
                        {formatToolName ? formatToolName(item.toolName) : item.toolName}
                      </Td>

                      <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">
                        {/* Doc Toggle */}
                        <div className="flex items-center gap-2">
                          {docToggleLoading[item.id] ? (
                            <div className="w-10 h-5 flex items-center justify-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#FF8E1C]"></div>
                            </div>
                          ) : (
                            <div
                              data-tooltip-id="session-tooltip"
                              data-tooltip-content={getToggleTooltip('docs', item)}
                            >
                              <Switcher
                                onChange={(checked) => onResourceToggle(checked, item.id)}
                                checked={!!item.supportedDocEnabled}
                                disabled={item.supportedDocEnabled || areActionsDisabled}
                              />
                            </div>
                          )}
                        </div>
                      </Td>

                      <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">
                        {/* Assignment Send */}
                        {item.assignmentEnabled ? (
                          <span
                            data-tooltip-id="session-tooltip"
                            data-tooltip-content={item.assignmentNames && item.assignmentNames.length > 18 ? item.assignmentNames : ''}
                          >
                            {truncateText(item.assignmentNames, 18)}
                          </span>
                        ) : (
                          <button
                            onClick={() => !areActionsDisabled && handleSendAssignment(item.id)}
                            className={`px-3 py-1 rounded text-xs flex items-center justify-center min-w-[50px] text-white ${areActionsDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#FF8E1C] hover:bg-[#E67D0F]'}`}
                            disabled={assignmentLoading[item.id] || areActionsDisabled}
                            data-tooltip-id="session-tooltip"
                            data-tooltip-content={getToggleTooltip('assignment', item)}
                          >
                            {assignmentLoading[item.id] ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                            ) : (
                              'Send'
                            )}
                          </button>
                        )}
                      </Td>

                      <Td className="text-start px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">
                        {/* Tool Toggle */}
                        <div className="flex items-center gap-2">
                          {toolToggleLoading[item.id] ? (
                            <div className="w-10 h-5 flex items-center justify-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#FF8E1C]"></div>
                            </div>
                          ) : (
                            <div
                              data-tooltip-id="session-tooltip"
                              data-tooltip-content={getToggleTooltip('tool', item)}
                            >
                              <Switcher
                                onChange={(checked) => onToolToggle(checked, item.id)}
                                checked={!!item.toolEnabled}
                                disabled={item.toolEnabled || areActionsDisabled}
                              />
                            </div>
                          )}
                        </div>
                      </Td>

                      <Td className="px-4 py-3 font-lato font-medium text-sm leading-[1.5] text-[#1D2939] whitespace-nowrap">
                        {/* Status Actions */}
                        <div className="flex gap-2 justify-center items-center">
                          {item.status?.toLowerCase() === 'cancelled' ? (
                            <button
                              disabled
                              className="text-red-600 opacity-50 cursor-not-allowed"
                              aria-label="Session cancelled"
                              data-tooltip-id="session-tooltip"
                              data-tooltip-content="You canceled the session already"
                            >
                              {/* Cancelled Icon */}
                              <svg width="16" height="16" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.6844 10L19.8296 3.88889C20.7235 3 20.7235 1.66667 19.8296 0.777778C19.4944 0.333333 18.9358 0 18.3771 0C17.8184 0 17.2598 0.222222 16.8129 0.666667L10.5559 6.88889L4.41061 0.777778C3.51676 -0.111111 2.06425 -0.111111 1.28212 0.777778C0.835195 1.11111 0.5 1.66667 0.5 2.33333C0.5 3 0.723464 3.44444 1.17039 3.88889L7.31564 10L1.17039 16.1111C0.835195 16.5556 0.5 17.1111 0.5 17.7778C0.5 18.3333 0.723464 18.8889 1.17039 19.3333C1.61732 19.7778 2.17598 20 2.73464 20C3.2933 20 3.85196 19.7778 4.29888 19.3333L10.4441 13.2222L16.5894 19.3333C17.4832 20.2222 18.9358 20.2222 19.7179 19.3333C20.6117 18.4444 20.6117 17 19.7179 16.2222L13.6844 10Z" fill="#B42318" /></svg>
                            </button>
                          ) : (
                            <>
                              {item.status == "completed" ? (
                                <button
                                  disabled
                                  className="text-green-600 opacity-50 cursor-not-allowed"
                                  aria-label="Assignment enabled"
                                  data-tooltip-id="session-tooltip"
                                  data-tooltip-content="You have sent the session already"
                                >
                                  {/* Check Icon */}
                                  <svg width="18" height="18" viewBox="0 0 25 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.5 10.7297L8.68692 17L22.5 3" stroke="#027A48" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                </button>
                              ) : (
                                <>
                                  <button onClick={() => handleAssignStatus(item.id, true)} className="text-green-600 hover:text-green-700">
                                    <svg width="18" height="18" viewBox="0 0 25 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.5 10.7297L8.68692 17L22.5 3" stroke="#027A48" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                  </button>
                                  <button onClick={() => handleAssignStatus(item.id, false)} className="text-red-600 hover:text-red-700">
                                    <svg width="14" height="14" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.6844 10L19.8296 3.88889C20.7235 3 20.7235 1.66667 19.8296 0.777778C19.4944 0.333333 18.9358 0 18.3771 0C17.8184 0 17.2598 0.222222 16.8129 0.666667L10.5559 6.88889L4.41061 0.777778C3.51676 -0.111111 2.06425 -0.111111 1.28212 0.777778C0.835195 1.11111 0.5 1.66667 0.5 2.33333C0.5 3 0.723464 3.44444 1.17039 3.88889L7.31564 10L1.17039 16.1111C0.835195 16.5556 0.5 17.1111 0.5 17.7778C0.5 18.3333 0.723464 18.8889 1.17039 19.3333C1.61732 19.7778 2.17598 20 2.73464 20C3.2933 20 3.85196 19.7778 4.29888 19.3333L10.4441 13.2222L16.5894 19.3333C17.4832 20.2222 18.9358 20.2222 19.7179 19.3333C20.6117 18.4444 20.6117 17 19.7179 16.2222L13.6844 10Z" fill="#B42318" /></svg>
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </Td>
                      <Td className="whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <CommonIconButton
                            type="view"
                            onClick={() => {
                              const targetCompanyId = item.companyId || apiParams.company_id;
                              dispatch(setsessionName(item.sessionName || ''));
                              navigate(`/allSessions/view?companyId=${targetCompanyId}&SessionId=${item.id}`);
                            }}
                          />
                          {item.sessionUrl && (
                            <p
                              className="w-10 h-10 flex items-center justify-center cursor-pointer"
                              onClick={() => {
                                setSelectedSessionLink({ generateUrl: item.sessionUrl });
                                setOpenCopyLinkModal(true);
                              }}
                            >
                              <CommonIconButton type="link" />
                            </p>
                          )}
                        </div>
                      </Td>
                    </Tr>
                  )
                })
              ) : (
                <Tr>
                  <Td colSpan={12} className="text-center font-lato font-medium text-sm leading-[1.5] text-[#1D2939]">
                    No matching records found
                  </Td>
                </Tr>
              )}
            </TBody>
          </Table>
        </div>
      </div>

      <div className="flex justify-between items-center mt-3">
        <Pagination
          totalCount={countData}
          pageNumber={apiParams.page}
          displayPage={allSessionsData?.length}
          UpdatePageLimit={(limit) => setApiParams((prev) => ({ ...prev, limit }))}
          UpdatePage={handlePageChange}
        />

        {countData > 0 && totalPages > 1 && (
          <ModernPagination
            totalPages={totalPages}
            currentPage={apiParams.page}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      <ConfirmationModal
        dialogOpen={confirmOpen}
        onDialogClose={() => setConfirmOpen(false)}
        title="Assignment Sent"
        message="The assignment has been sent successfully."
      />

      <CopyLinkURLModal
        dialogOpen={openCopyLinkModal}
        onDialogClose={() => setOpenCopyLinkModal(false)}
        onSubmit={() => setOpenCopyLinkModal(false)}
        companyData={selectedSessionLink}
      />
    </React.Fragment>
  );
};

export default BatchesSessions;